import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/constants";
import { PackageCheck, Loader2, AlertTriangle, CircleUser, ArrowRight, MessageSquareText } from "lucide-react";

// ITEM 20 — Delivery shadow. A scheduled milestone came due and its status is still Forecast:
// nobody captured what happened. Rex names the OWNER and asks — it never invents the outcome.

export default function DeliveryShadow() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/delivery-shadow`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8" data-testid="delivery-shadow-page">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-cyan-50 p-2 text-cyan-600"><PackageCheck className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Delivery shadow</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Milestones whose date has passed but whose status is still <span className="font-medium text-slate-700">Forecast</span> —
            nobody captured what happened. Rex asks the owner; it never guesses the outcome.
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-10 flex items-center gap-2 text-sm text-slate-400" data-testid="delivery-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking milestones…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="mt-5 flex items-center gap-4 text-xs text-slate-500" data-testid="delivery-summary">
            <span><span className="font-semibold text-slate-900">{data.silent_milestones}</span> milestone(s) with no captured outcome</span>
            <span className="text-slate-300">•</span>
            <span>{data.opportunities_with_silent_milestones} deal(s)</span>
            {data.unowned > 0 && (<><span className="text-slate-300">•</span><span className="text-amber-600">{data.unowned} unowned</span></>)}
          </div>

          {data.items.length === 0 ? (
            <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center" data-testid="delivery-empty">
              <p className="text-sm font-medium text-slate-700">Every scheduled milestone has a captured outcome.</p>
              <p className="mt-1 text-sm text-slate-500">Nothing in the shadow right now.</p>
            </div>
          ) : (
            <div className="mt-6 space-y-3" data-testid="delivery-list">
              {data.items.map((it) => (
                <div key={it.opportunity_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`delivery-item-${it.opportunity_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <Link to={`/clients/${it.client_id}`} className="text-base font-semibold text-slate-900 hover:underline" data-testid="delivery-client-link">
                      {it.company_name}
                    </Link>
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="delivery-owner">
                      <CircleUser className="h-3.5 w-3.5" />
                      {it.owner?.name ? <>Owner: <span className="font-medium text-slate-700">{it.owner.name}</span></> : <span className="text-amber-600">Unassigned — needs an owner</span>}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-400">{it.opportunity_name}{it.restricted ? " · restricted (internal only — do not contact)" : ""}</div>

                  <div className="mt-3 space-y-2">
                    {it.due_milestones.map((m) => (
                      <div key={m.entry_id} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2" data-testid="delivery-milestone">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        <div className="text-sm text-amber-900">{m.prompt}</div>
                      </div>
                    ))}
                  </div>

                  {it.next_conversation && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-50 px-3 py-2" data-testid="delivery-next-conversation">
                      <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
                      <p className="text-sm text-indigo-900">{it.next_conversation}</p>
                    </div>
                  )}

                  <div className="mt-3 flex items-center text-xs">
                    <Link to={`/clients/${it.client_id}`} className="ml-auto inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="delivery-open">
                      Capture outcome <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
